// v2/components/QuickView.js — satir uzerine gelince acilan hizli siparis penceresi
import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { Minus, Plus, Zap, FileText, Warehouse, Hash, Car, Package } from 'lucide-react';
import { WAREHOUSES } from '../constants/catalog';
import { useMiktarTaslak } from '../utils/miktarTaslak';
import { FitmentLine, FitmentLogos } from './FitmentInfo';
import { formatTRY, formatNumber, formatPercent } from '../utils/format';
import { withVat } from '../utils/discount';
import styles from './QuickView.module.css';

const CARD_W = 320;
const OFFSET = 14;
const M = 12;

/** Basili tutunca hizlanarak tekrar eden aksiyon */
function useHoldRepeat(action) {
  const timer = useRef(null);
  const count = useRef(0);

  const stop = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    count.current = 0;
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback(() => {
    action();
    const tick = () => {
      count.current += 1;
      action();
      timer.current = setTimeout(tick, count.current > 6 ? 40 : 90);
    };
    timer.current = setTimeout(tick, 400);
  }, [action]);

  return {
    onPointerDown: (e) => { if (e.button === 0 || e.pointerType !== 'mouse') start(); },
    onPointerUp: stop,
    onPointerLeave: stop,
    onPointerCancel: stop,
  };
}

/** Degisimde sayarak gecen deger */
function useCountUp(target, duration = 380) {
  const [value, setValue] = useState(target);
  const raf = useRef(null);
  const from = useRef(target);

  useEffect(() => {
    const reduce = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !Number.isFinite(target)) { setValue(target); from.current = target; return undefined; }

    const start = from.current;
    const delta = target - start;
    if (delta === 0) return undefined;

    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const tick = (now) => {
      const pr = Math.min((now - t0) / duration, 1);
      setValue(start + delta * (1 - Math.pow(1 - pr, 3)));
      if (pr < 1) raf.current = requestAnimationFrame(tick);
      else from.current = target;
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target, duration]);

  return value;
}

export default function QuickView({
  product, anchor, eurRate = 0, onAddToCart, onOpenDetail, onEnter, onLeave,
}) {
  const cardRef = useRef(null);
  const [pos, setPos] = useState({ top: -9999, left: -9999 });
  const [whKey, setWhKey] = useState(null);
  const [qty, setQty] = useState(1);
  const taslak = useMiktarTaslak(product ? (product.productCode || product.code) : null);

  // Kartin GERCEK olculen yuksekligine gore konumlandir
  useLayoutEffect(() => {
    if (!anchor) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const el = cardRef.current;
    const w = el ? el.offsetWidth : CARD_W;
    const h = el ? el.offsetHeight : 540;

    // Panel tablonun hemen sagina yaslanir; tasarsa ekran kenarina cekilir
    let left = anchor.x + OFFSET;
    if (left + w > vw - M) left = vw - w - M;
    let top = anchor.y - h / 2;

    if (left < M) left = M;
    if (top < M) top = M;
    if (top + h > vh - M) top = Math.max(M, vh - h - M);

    setPos({ top, left });
  }, [anchor, product]);

  // Listede girilen depo+miktar varsa onunla acilir; kart acikken satir degisirse aninda guncellenir.
  useEffect(() => {
    const t = taslak && product[taslak.whKey] > 0 ? taslak : null;
    if (t) {
      setWhKey(t.whKey);
      setQty(Math.min(t.qty, Math.max(product[t.whKey], 1)));
      return;
    }
    const first = WAREHOUSES.find((w) => product[w.key] > 0);
    setWhKey(first ? first.key : WAREHOUSES[0].key);
    setQty(1);
  }, [product, taslak]);

  // --- hook'lar erken return'den once calismali ---
  const safeWh = WAREHOUSES.find((w) => w.key === whKey) || WAREHOUSES[0];
  const safeStock = product ? product[safeWh.key] : 0;
  const safeTotal = product ? withVat(product.netPrice * qty) : 0;
  const safeSaving = product ? product.savingAmount * qty : 0;

  const decQty = useCallback(() => setQty((q) => Math.max(q - 1, 1)), []);
  const incQty = useCallback(
    () => setQty((q) => Math.min(q + 1, Math.max(safeStock, 1))),
    [safeStock]
  );
  const holdDec = useHoldRepeat(decQty);
  const holdInc = useHoldRepeat(incQty);

  const shownTotal = useCountUp(safeTotal);
  const shownSaving = useCountUp(safeSaving);

  if (!product || !anchor) return null;

  const wh = WAREHOUSES.find((w) => w.key === whKey) || WAREHOUSES[0];
  const stock = product[wh.key];
  const out = stock <= 0;
  const totalVat = withVat(product.netPrice * qty);
  const eur = (v) => (eurRate > 0 ? `${formatNumber(v / eurRate, 2)} €` : null);

  return (
    <div
      ref={cardRef}
      className={styles.pop}
      style={{ top: pos.top, left: pos.left, width: CARD_W }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      role="dialog"
    >
      <div className={styles.head}>
        <div className={styles.headRow}>
          <span className={styles.thumb}>
            {product.image ? (
              <img className={styles.thumbImg} src={product.image} alt="" loading="lazy" decoding="async" />
            ) : (
              <Package size={26} strokeWidth={1.3} />
            )}
          </span>
          <div className={styles.headText}>
            <div className={styles.headTop}>
              <span className={styles.code}>{product.productCode}</span>
              {(product.discounts || []).some((d) => d.kind === 'general')
                && <span className={styles.campTag}>KAMPANYALI</span>}
            </div>
            <h3 className={styles.name}>{product.description}</h3>
          </div>
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.lineRow}>
          <span className={styles.lineLabel}>Brüt Birim</span>
          <span className={styles.strike}>{formatTRY(product.grossPrice)}</span>
        </div>

        <div className={styles.chainBox}>
          <div className={styles.chainHead}>
            <span className={styles.chainTitle}>İndirim Zinciri</span>
            <span className={styles.chainTotal}>Toplam {formatPercent(product.discountRate)}</span>
          </div>
          <div className={styles.chips}>
            {product.discounts.map((d, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className={styles.arrow}>›</span>}
                <span className={`${styles.chip} ${d.kind === 'general' ? styles.chipCamp : ''}`}>
                  %{formatNumber(d.rate, 2)}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className={styles.netBox}>
          <span className={styles.netLabel}>NET BİRİM</span>
          <span className={styles.netRight}>
            <span className={styles.netVal}>{formatTRY(product.netPrice)}</span>
            {eurRate > 0 && <div className={styles.netEur}>{eur(product.netPrice)}</div>}
          </span>
        </div>

        <div className={styles.saveBox}>
          <span className={styles.saveLabel}>Kazancınız</span>
          <span className={styles.saveVal}>{formatTRY(shownSaving)}</span>
        </div>

        <p className={styles.sectionTitle}><Warehouse size={10} /> Depo Seçimi</p>
        <div className={styles.whPick}>
          {WAREHOUSES.map((w) => {
            const wOut = product[w.key] <= 0;
            const on = w.key === whKey;
            return (
              <button
                key={w.key} type="button" disabled={wOut}
                className={`${styles.whOpt} ${on ? styles.whOptOn : ''}`}
                onClick={() => { setWhKey(w.key); setQty(1); }}
              >
                <span className={`${styles.radio} ${on ? styles.radioOn : ''}`}>
                  {on && <span className={styles.radioDot} />}
                </span>
                <span>
                  <span className={styles.whName}>{w.city}</span>
                  <span className={wOut ? styles.whStateOut : styles.whState}>
                    {wOut ? 'Stok Yok' : 'Mevcut'}
                  </span>
                </span>
              </button>
            );
          })}
        </div>

        <div className={styles.qtyRow}>
          <span>
            <p className={styles.sectionTitle}>Miktar</p>
            <span className={styles.qtyBox}>
              <button type="button" className={styles.qtyBtn} {...holdDec}
                      disabled={qty <= 1 || out} aria-label="Azalt">
                <Minus size={13} />
              </button>
              <span className={styles.qtyVal}>{out ? 0 : qty}</span>
              <button type="button" className={styles.qtyBtn} {...holdInc}
                      disabled={qty >= stock || out} aria-label="Artır">
                <Plus size={13} />
              </button>
            </span>
          </span>
          <span className={styles.totalRight}>
            <span className={styles.totalLabel}>Toplam (KDV Dahil)</span>
            <div className={styles.totalVal}>{formatTRY(shownTotal)}</div>
            {eurRate > 0 && <div className={styles.totalEur}>{eur(shownTotal)}</div>}
          </span>
        </div>

        <div className={styles.btnRow}>
          <button type="button" className={styles.btnGhost} onClick={() => onOpenDetail(product)}>
            <FileText size={14} /> Detaylı Sipariş
          </button>
          <button
            type="button" className={styles.btnMain} disabled={out}
            onClick={() => onAddToCart(product, qty, wh)}
          >
            <Zap size={14} /> Hızlı Sipariş
          </button>
        </div>

        <p className={styles.sectionTitle}><Hash size={10} /> OEM Kodları</p>
        {product.oem && product.oem.length ? (
          <div className={styles.oemWrap}>
            {product.oem.map((o) => <span key={o} className={styles.oemChip}>{o}</span>)}
          </div>
        ) : <span className={styles.muted}>Kayıt bulunmuyor</span>}

        <p className={styles.sectionTitle}><Car size={10} /> Araç Uyumu</p>
        <FitmentLogos code={product.productCode} limit={6} size="sm" />
        <FitmentLine code={product.productCode} className={styles.fitLine} />
      </div>
    </div>
  );
}
